import {
  Button,
  cn,
  createDataTableColumnHelper,
  DataTable,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  Link as UiLink,
} from "@beweegbaas/ui";
import { createFileRoute, stripSearchParams } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod/v4";
import { ErrorBoundaryComponent } from "#/components/error-boundary";
import { Page } from "#/components/page";
import { PendingContent } from "#/components/pending-content";
import { TicketDialog } from "#/modules/tickets/components/ticket-dialog";
import { TicketStatusBadge } from "#/modules/tickets/components/ticket-status-badge";
import { formatRelativeTime } from "#/modules/util.helpers/format";
import { getTicketsFn } from "#/modules/tickets/tickets.functions";
import { m } from "#/paraglide/messages";

// Deep-link params for a prefilled "Nieuw ticket" dialog (e.g. the docenten page's
// "meer dan X docenten" popover): /tickets?subject=...&message=... opens the dialog seeded.
const ticketsSearchSchema = z.object({
  subject: z.string().default(""),
  message: z.string().default(""),
});
const TICKETS_SEARCH_DEFAULTS = { subject: "", message: "" };

export const Route = createFileRoute("/$locale/{-$schoolyear}/_authenticated/$school/tickets/")({
  validateSearch: ticketsSearchSchema,
  search: { middlewares: [stripSearchParams(TICKETS_SEARCH_DEFAULTS)] },
  loader: () => getTicketsFn(),
  component: UserTicketsOverview,
  errorComponent: UserTicketsError,
});

function UserTicketsError({ error }: { error: unknown }) {
  const params = Route.useParams();
  return <ErrorBoundaryComponent error={error} params={params} />;
}

type Ticket = Awaited<ReturnType<typeof getTicketsFn>>[number];

const helper = createDataTableColumnHelper<Ticket>();

function NewTicketButton() {
  return (
    <TicketDialog
      trigger={
        <Button size="xl">
          <IconMaterialSymbolsAdd />
          {m["tickets.list.new_ticket"]()}
        </Button>
      }
    />
  );
}

const escapeHtml = (text: string) =>
  text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/** Each line of a deep-linked `message` becomes its own paragraph in the editor. */
const toParagraphs = (text: string) =>
  text
    .split(/\r?\n/)
    .map((line) => `<p>${escapeHtml(line)}</p>`)
    .join("");

function UserTicketsOverview() {
  const tickets = Route.useLoaderData();
  const { locale, schoolyear, school } = Route.useParams();
  const { subject, message } = Route.useSearch();
  const navigate = Route.useNavigate();

  // Capture the deep-link prefill once — the params clear right after mount so a refresh or
  // back-navigation doesn't reopen the dialog.
  const [prefill] = useState(() =>
    subject || message ? { subject, content: message ? toParagraphs(message) : "" } : null,
  );
  useEffect(() => {
    if (!subject && !message) return;
    void navigate({
      search: (prev) => ({ ...prev, subject: "", message: "" }),
      replace: true,
      resetScroll: false,
    });
  }, [subject, message, navigate]);

  const columns = useMemo(
    () =>
      helper.columns([
        helper.accessor("ticketNumber", {
          header: () => m["common.labels.ticket"](),
          meta: { rowTarget: true },
          cell: ({ row }) => (
            <UiLink
              variant="secondary"
              underline
              to="/$locale/{-$schoolyear}/$school/tickets/$id"
              params={(prev: Record<string, string>) => ({ ...prev, id: row.original.id })}
              // No hover-preload: the detail loader pulls the full message thread.
              preload={false}
              className={cn(
                "flex items-center gap-2 whitespace-nowrap",
                row.original.hasUnread && "font-bold",
              )}
            >
              {row.original.hasUnread && (
                <span
                  data-testid="ticket-unread-dot"
                  className="size-2 shrink-0 rounded-full bg-linear-to-br from-red-600 to-red-800"
                />
              )}
              #{row.original.ticketNumber}
            </UiLink>
          ),
        }),
        helper.accessor("subject", {
          header: () => m["common.fields.onderwerp"](),
          cell: ({ getValue, row }) => (
            <span
              className={cn("block max-w-[40ch] truncate", row.original.hasUnread && "font-bold")}
            >
              {getValue()}
            </span>
          ),
        }),
        helper.accessor("status", {
          header: () => m["common.fields.status"](),
          cell: ({ getValue }) => <TicketStatusBadge status={getValue()} />,
        }),
        helper.accessor("lastActivityAt", {
          header: () => m["common.labels.laatste_activiteit"](),
          cell: ({ getValue }) => (
            <span className="whitespace-nowrap">{formatRelativeTime(getValue())}</span>
          ),
        }),
      ]),
    [locale, schoolyear, school],
  );

  return (
    <Page>
      {/* Deep-linked tickets have a fixed purpose; FAQ deflection would only add noise. */}
      {prefill && <TicketDialog defaultOpen initialValues={prefill} showFaqSuggestions={false} />}
      <Page.Header>
        <Page.Title>{m["tickets.list.title"]()}</Page.Title>
        <Page.Header.Actions>
          <Page.Header.MainAction>
            <NewTicketButton />
          </Page.Header.MainAction>
        </Page.Header.Actions>
      </Page.Header>
      <PendingContent>
        {tickets.length === 0 ? (
          <Empty>
            <EmptyHeader>
              <EmptyTitle>{m["tickets.list.empty_title"]()}</EmptyTitle>
              <EmptyDescription>{m["tickets.list.empty_description"]()}</EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <NewTicketButton />
            </EmptyContent>
          </Empty>
        ) : (
          <DataTable
            alignToContainer
            aria-label={m["tickets.list.title"]()}
            columns={columns}
            data={tickets}
            getRowId={(ticket) => ticket.id}
            empty={m["tickets.list.empty_title"]()}
          />
        )}
      </PendingContent>
    </Page>
  );
}
